"use client"

import { useEffect } from "react"
import { reloadStaleDeploymentManually } from "@/shared/lib/stale-deployment"
import { useStaleDeploymentRecovery } from "@/shared/hooks/use-stale-deployment-recovery"
import { captureClientException } from "@/shared/lib/monitoring/capture-client"

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const { isStale, showReloadCta } = useStaleDeploymentRecovery(error)

  useEffect(() => {
    captureClientException(error, {
      scope: "client-render",
      extra: { digest: error.digest, boundary: "global-root" },
    })
  }, [error])

  const title = isStale ? "App updated" : "Something went wrong"
  const description = showReloadCta
    ? "A new version is available. Reload to continue."
    : isStale
      ? "Reloading to get the latest version…"
      : "An unexpected error occurred. Please try again."
  const showActions = !isStale || showReloadCta

  return (
    <html lang="en" className="dark">
      <body style={{ background: "#000", color: "#fff", minHeight: "100vh" }}>
        <main
          style={{
            display: "flex",
            minHeight: "100vh",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            textAlign: "center",
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}
        >
          <h1 style={{ fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.5rem" }}>
            {title}
          </h1>
          <p style={{ color: "#a1a1aa", marginBottom: "2rem", maxWidth: 420 }}>
            {description}
          </p>
          {error.digest && !isStale && (
            <p style={{ color: "#71717a", fontSize: "0.75rem", marginBottom: "1.5rem" }}>
              Error ID: {error.digest}
            </p>
          )}
          {showActions && (
            <button
              onClick={showReloadCta ? reloadStaleDeploymentManually : reset}
              style={{
                padding: "0.5rem 1.25rem",
                borderRadius: "0.5rem",
                border: "1px solid #1d7df2",
                background: "#1d7df2",
                color: "#fff",
                cursor: "pointer",
              }}
            >
              {showReloadCta ? "Reload" : "Try Again"}
            </button>
          )}
        </main>
      </body>
    </html>
  )
}
